/* =============================================================
   SelfApplyPage — /visa/:country/self
   Dedicated page for "Өөрөө мэдүүлэх" flow
   Shows: bundle upsell card + individual visa type options
   ============================================================= */
import { useParams, useLocation } from "wouter";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  ArrowLeft, ArrowRight, Plane, GraduationCap,
  Briefcase, Globe, Zap, Tag, CheckCircle2,
} from "lucide-react";
import Header from "@/components/Header";
import OrderModal from "@/components/OrderModal";

/* ── Country meta ─────────────────────────────────────────────── */
const COUNTRY_META: Record<string, { flag: string; name: string }> = {
  usa:        { flag: "🇺🇸", name: "АНУ" },
  australia:  { flag: "🇦🇺", name: "Австрали" },
  canada:     { flag: "🇨🇦", name: "Канад" },
  ireland:    { flag: "🇮🇪", name: "Ирланд" },
  czech:      { flag: "🇨🇿", name: "Чех Улс" },
  schengen:   { flag: "🇪🇺", name: "Шенген" },
  uk:         { flag: "🇬🇧", name: "Их Британи" },
  newzealand: { flag: "🇳🇿", name: "Шинэ Зеланд" },
};

/* ── Sub-options per country ──────────────────────────────────── */
interface SubOption {
  label: string;
  icon: React.ElementType;
  color: string;
  route: string;
  badge?: string;
  price: string;
  includes: string[];
}

const SELF_SUB_OPTIONS: Record<string, SubOption[]> = {
  usa: [
    {
      label: "Аяллын виз (B1/B2)",
      icon: Plane,
      color: "#60a5fa",
      route: "/visa/usa/travel/self-guide",
      price: "₮59,000",
      includes: ["DS-160 бөглөх заавар", "Ярилцлагын бэлтгэл", "Бонд буцааж авах"],
    },
    {
      label: "Сургалтын виз (F-1)",
      icon: GraduationCap,
      color: "#34d399",
      route: "/visa/usa/study/self-guide",
      price: "₮59,000",
      includes: ["Сургуулийн сонголт", "I-20 & SEVIS заавар", "OPT/CPT тайлбар"],
    },
    {
      label: "Ажлын виз / Ногоон карт",
      icon: Briefcase,
      color: "#f59e0b",
      route: "/visa/usa/work/work-guide",
      price: "₮69,000",
      badge: "EB3/NIW/H1B",
      includes: ["EB3 Unskilled заавар", "NIW өргөдлийн заавар", "H1B/H2B тайлбар"],
    },
    {
      label: "Цагаачлал",
      icon: Globe,
      color: "#a78bfa",
      route: "/visa/usa/work/immigration",
      price: "₮79,000",
      includes: ["Цагаачлалын боломжууд", "Өмгөөлөгчтэй холбох", "Орогнолын асуудал"],
    },
  ],
  australia: [
    {
      label: "Аяллын виз",
      icon: Plane,
      color: "#60a5fa",
      route: "/visa/australia/travel/self-guide",
      price: "₮49,000",
      includes: ["Online profile нээх", "Материалын тайлбар", "Татгалзлыг бууруулах"],
    },
    {
      label: "Сургалтын виз",
      icon: GraduationCap,
      color: "#34d399",
      route: "/visa/australia/study/self-guide",
      price: "₮59,000",
      includes: ["CoE бичиг баримт", "Эрүүл мэндийн шалгалт", "Ажил эрхлэх боломж"],
    },
    {
      label: "Цагаачлал / Ажлын виз",
      icon: Globe,
      color: "#a78bfa",
      route: "/visa/australia/work/immigration",
      price: "₮79,000",
      includes: ["Subclass 189/482/186", "Цагаачлалын өмгөөлөгч", "Ажлын визний төрөл"],
    },
  ],
  canada: [
    {
      label: "Аяллын виз",
      icon: Plane,
      color: "#60a5fa",
      route: "/visa/canada/travel/self-guide",
      price: "₮49,000",
      includes: ["GCKEY profile нээх", "Захидал бичих", "Санхүүгийн баталгаа"],
    },
    {
      label: "Сургалтын виз",
      icon: GraduationCap,
      color: "#34d399",
      route: "/visa/canada/study/self-guide",
      price: "₮59,000",
      includes: ["Study permit заавар", "Биометр бүртгэл", "PGWP тайлбар"],
    },
    {
      label: "Цагаачлал (Express Entry)",
      icon: Globe,
      color: "#a78bfa",
      route: "/visa/canada/work/immigration",
      price: "₮79,000",
      includes: ["Express Entry систем", "Оноо нэмэгдүүлэх", "Цагаачлалын өмгөөлөгч"],
    },
  ],
  ireland: [
    {
      label: "Аяллын виз",
      icon: Plane,
      color: "#60a5fa",
      route: "/visa/ireland/travel/self-guide",
      price: "₮49,000",
      includes: ["Материал бүрдүүлэх", "Санхүүгийн баталгаа", "Татгалзлыг бууруулах"],
    },
    {
      label: "Сургалт, ажлын виз",
      icon: GraduationCap,
      color: "#34d399",
      route: "/visa/ireland/study/self-guide",
      price: "₮59,000",
      includes: ["Гэрээт сургуулиуд", "20 цаг ажиллах боломж", "Гэр бүлээрээ явах"],
    },
  ],
  // Czech has no self-apply option — only professional service
  czech: [],
  schengen: [
    {
      label: "Аяллын виз",
      icon: Plane,
      color: "#60a5fa",
      route: "/visa/schengen/travel/self-guide",
      price: "₮49,000",
      includes: ["Элчин сайдын газар сонгох", "Материал бүрдүүлэх", "Санхүүгийн баталгаа"],
    },
  ],
  uk: [
    {
      label: "Аяллын виз",
      icon: Plane,
      color: "#60a5fa",
      route: "/visa/uk/travel/self-guide",
      price: "₮49,000",
      includes: ["Материал бүрдүүлэх", "Санхүүгийн баталгаа", "Татгалзлыг бууруулах"],
    },
    {
      label: "Сургалтын виз",
      icon: GraduationCap,
      color: "#34d399",
      route: "/visa/uk/study/self-guide",
      price: "₮59,000",
      includes: ["CAS бичиг баримт", "20 цаг ажиллах боломж", "Тэтгэлэгт зуучлах"],
    },
  ],
  newzealand: [
    {
      label: "Мэргэжлээрээ ажиллах",
      icon: Briefcase,
      color: "#f59e0b",
      route: "/visa/newzealand/work/work-guide",
      price: "₮69,000",
      badge: "Trending",
      includes: ["Skilled Migrant Category", "Мэргэжлийг хүлээн зөвшөөрүүлэх", "Хуулийн фирмтэй хамтарч"],
    },
    {
      label: "Аяллын виз",
      icon: Plane,
      color: "#60a5fa",
      route: "/visa/newzealand/travel/self-guide",
      price: "₮49,000",
      includes: ["Материал бүрдүүлэх", "Санхүүгийн баталгаа", "Аяллын зорилго"],
    },
    {
      label: "Сургалтын виз",
      icon: GraduationCap,
      color: "#34d399",
      route: "/visa/newzealand/study/self-guide",
      price: "₮59,000",
      includes: ["Гэрээт сургуулиуд", "Ажил эрхлэх боломж", "Тэтгэлэгт зуучлах"],
    },
  ],
};

/* ── Bundle visa key per country (for OrderModal) ────────────── */
const BUNDLE_VISA_KEY: Record<string, string> = {
  usa:        "usa-self-bundle",
  australia:  "australia-self-bundle",
  canada:     "canada-self-bundle",
  ireland:    "ireland-self-bundle",
  schengen:   "schengen-self-bundle",
  uk:         "usa-self-bundle",
  newzealand: "newzealand-self-bundle",
  default:    "usa-self-bundle",
};

/* ── Main page ────────────────────────────────────────────────── */
export default function SelfApplyPage() {
  const params = useParams<{ country: string }>();
  const [, navigate] = useLocation();

  const [orderOpen, setOrderOpen] = useState(false);

  const country    = COUNTRY_META[params.country] ?? { flag: "🌍", name: params.country };
  const subOptions = SELF_SUB_OPTIONS[params.country] ?? [];
  const bundleVisaKey = BUNDLE_VISA_KEY[params.country] ?? BUNDLE_VISA_KEY.default;

  const totalIndividual = subOptions.reduce((sum, opt) => {
    const num = parseInt(opt.price.replace(/[₮,]/g, ""), 10);
    return sum + (isNaN(num) ? 0 : num);
  }, 0);
  const totalFormatted = `₮${totalIndividual.toLocaleString()}`;

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #130D3A 45%, #0F0B35 100%)" }}
    >
      <Header />

      <div className="container pt-24 pb-16 max-w-2xl mx-auto">
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(`/visa/${params.country}`)}
          className="flex items-center gap-2 mb-8 text-white/50 hover:text-white/80 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Буцах</span>
        </motion.button>

        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <span className="text-5xl mb-4 block">{country.flag}</span>
          <h1
            className="text-white font-black text-3xl leading-tight mb-2"
            style={{ fontFamily: "'Sora', sans-serif", letterSpacing: "-0.02em" }}
          >
            {country.name} — Өөрөө мэдүүлэх
          </h1>
          <p className="text-white/50 text-sm">
            Визийн төрлөө сонгоно уу
          </p>
        </motion.div>

        <div className="flex flex-col gap-4">

          {/* ── Bundle upsell card ── */}
          {subOptions.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="rounded-2xl overflow-hidden"
              style={{
                background: "linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(234,88,12,0.07) 100%)",
                border: "1px solid rgba(245,158,11,0.30)",
              }}
            >
              <div className="p-5">
                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
                    style={{ background: "rgba(245,158,11,0.18)", color: "#fbbf24" }}
                  >
                    <Zap className="w-3 h-3" />
                    ХАМГИЙН АШИГТАЙ
                  </div>
                  <div
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
                    style={{ background: "rgba(239,68,68,0.13)", color: "#f87171" }}
                  >
                    <Tag className="w-3 h-3" />
                    Хэмнэлт: {totalFormatted}-с дээш
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-white font-bold text-lg mb-1">
                  Бүх зааварчилгааг нэгтгэсэн багц
                </h3>
                <p className="text-white/50 text-sm mb-4 leading-relaxed">
                  Тус тусад нь авбал <span className="text-white/70">{totalFormatted}</span> болно. Багцаар авбал:
                </p>

                {/* Individual items strikethrough */}
                <div className="flex flex-col gap-2 mb-5">
                  {subOptions.map((opt, i) => {
                    const Icon = opt.icon;
                    return (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: opt.color }} />
                          <span className="text-white/50 text-sm">{opt.label}</span>
                        </div>
                        <span className="text-white/30 text-sm line-through">{opt.price}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Price + CTA */}
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-white/40 text-xs mb-0.5">Багцын үнэ</div>
                    <div
                      className="font-black text-3xl text-white"
                      style={{ fontFamily: "'Sora', sans-serif" }}
                    >
                      ₮129,000
                    </div>
                  </div>
                  <button
                    onClick={() => setOrderOpen(true)}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all"
                    style={{
                      background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)",
                      color: "white",
                      boxShadow: "0 4px 20px rgba(245,158,11,0.40)",
                    }}
                  >
                    <Zap className="w-4 h-4" />
                    Багцаар авах
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ── Divider ── */}
          {subOptions.length > 1 && (
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.07)" }} />
              <span className="text-white/30 text-xs">эсвэл тусад нь сонгоно уу</span>
              <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.07)" }} />
            </div>
          )}

          {/* ── Individual sub-options ── */}
          {subOptions.map((opt, i) => {
            const Icon = opt.icon;
            return (
              <motion.button
                key={i}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(opt.route)}
                className="w-full rounded-2xl overflow-hidden text-left transition-all duration-200"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `${opt.color}18`, border: `1px solid ${opt.color}30` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: opt.color }} />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-base leading-tight">{opt.label}</h3>
                        {opt.badge && (
                          <span
                            className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block"
                            style={{ background: `${opt.color}20`, color: opt.color }}
                          >
                            {opt.badge}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-white/60 font-semibold text-sm">{opt.price}</span>
                      <ArrowRight className="w-4 h-4 text-white/25" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {opt.includes.map((item, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: opt.color, opacity: 0.7 }} />
                        <span className="text-white/45 text-xs">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.button>
            );
          })}

        </div>
      </div>

      {/* OrderModal for bundle purchase */}
      <OrderModal
        isOpen={orderOpen}
        onClose={() => setOrderOpen(false)}
        preselectedVisaKey={bundleVisaKey}
        orderSource="self"
      />
    </div>
  );
}
